import { useEffect, useMemo, useState } from "react";
import { useGetList, useListContext } from "ra-core";
import {
  Building2,
  Check,
  ChevronDown,
  Filter,
  Search,
  User as UserIcon,
  Users,
} from "lucide-react";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import type { Sale } from "../types";
import { defaultCompanySectors } from "../root/defaultConfiguration";
import { sizes } from "./sizes";

// Sprint 8 — FilterBar horizontale pour /companies conforme ent-shared.jsx.
// Remplace la sidebar verticale CompanyListFilter (Sprint 7) pour matcher la
// maquette E_D1 (table dense + filtres horizontaux).

type DropdownOption = {
  value: string | null;
  label: string;
  dot?: string;
};

type DropdownConfig = {
  id: string;
  filterKey: string;
  icon: React.ReactNode;
  label: string;
  options: ReadonlyArray<DropdownOption>;
  useDotWhenActive?: boolean;
};

const TEMP_OPTIONS: DropdownOption[] = [
  { value: null, label: "Toutes" },
  { value: "chaud", label: "Chaud", dot: "bg-red-500" },
  { value: "a_chauffer", label: "À chauffer", dot: "bg-orange-500" },
  { value: "froid", label: "Froid", dot: "bg-blue-500" },
  { value: "perdu", label: "Perdu", dot: "bg-muted-foreground" },
];

const POPULATION_OPTIONS: DropdownOption[] = [
  { value: null, label: "Toutes" },
  { value: "plateforme", label: "Plateforme", dot: "bg-emerald-500" },
  { value: "prospection", label: "Prospection", dot: "bg-orange-500" },
];

const SIZE_OPTIONS: DropdownOption[] = [
  { value: null, label: "Toutes" },
  ...sizes.map((s) => ({ value: String(s.id), label: s.name })),
];

export function CompanyFilterBar({
  shownCount,
  totalCount,
}: {
  shownCount: number;
  totalCount: number;
}) {
  const { filterValues, setFilters, refetch } = useListContext();
  const [searchInput, setSearchInput] = useState<string>(
    () => (filterValues?.["name@ilike"] as string) ?? "",
  );

  // Source: defaultCompanySectors (root/defaultConfiguration). Évite la
  // duplication entre dropdown et formulaire CompanyInputs.
  const SECTOR_OPTIONS: DropdownOption[] = [
    { value: null, label: "Tous" },
    ...defaultCompanySectors.map((s) => ({ value: s.value, label: s.label })),
  ];

  // Sales pour Resp. compte dropdown
  const { data: salesList } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 50 },
    sort: { field: "first_name", order: "ASC" },
    filter: { disabled: false },
  });
  const RESP_OPTIONS: DropdownOption[] = useMemo(
    () => [
      { value: null, label: "Tous" },
      ...(salesList ?? []).map((s) => ({
        value: String(s.id),
        label: `${s.first_name ?? ""} ${s.last_name ?? ""}`.trim() || "—",
      })),
    ],
    [salesList],
  );

  const DROPDOWNS: DropdownConfig[] = [
    {
      id: "size",
      filterKey: "size",
      icon: <Building2 className="size-3.5" />,
      label: "Taille",
      options: SIZE_OPTIONS,
    },
    {
      id: "sector",
      filterKey: "sector",
      icon: <Filter className="size-3.5" />,
      label: "Secteur",
      options: SECTOR_OPTIONS,
    },
    // Population et Température n'existent pas sur `companies` directement —
    // on les expose comme filtres "soft" appliqués sur la jointure
    // companies_temperature_summary (côté frontend) via filterValues.
    {
      id: "population",
      filterKey: "_population",
      icon: <Users className="size-3.5" />,
      label: "Population",
      options: POPULATION_OPTIONS,
    },
    {
      id: "temperature",
      filterKey: "_temperature",
      icon: <Filter className="size-3.5" />,
      label: "Température",
      options: TEMP_OPTIONS,
      useDotWhenActive: true,
    },
    {
      id: "sales_id",
      filterKey: "sales_id",
      icon: <UserIcon className="size-3.5" />,
      label: "Resp. compte",
      options: RESP_OPTIONS,
    },
  ];

  // Debounce search → filterValues["name@ilike"]
  useEffect(() => {
    const handle = window.setTimeout(() => {
      const current = (filterValues?.["name@ilike"] as string) ?? "";
      if (searchInput === current) return;
      const next = { ...(filterValues ?? {}) };
      if (searchInput.trim()) {
        next["name@ilike"] = `%${searchInput.trim()}%`;
      } else {
        delete next["name@ilike"];
      }
      setFilters?.(next, []);
    }, 250);
    return () => window.clearTimeout(handle);
  }, [searchInput, filterValues, setFilters]);

  const applyDropdown = (filterKey: string, value: string | null) => {
    const next = { ...(filterValues ?? {}) };
    if (value === null) {
      delete next[filterKey];
    } else {
      next[filterKey] = filterKey === "size" ? Number(value) : value;
    }
    setFilters?.(next, []);
  };

  const filterKeys = DROPDOWNS.map((d) => d.filterKey);
  const hasActive = filterKeys.some(
    (k) =>
      filterValues?.[k] !== undefined &&
      filterValues?.[k] !== null &&
      filterValues?.[k] !== "",
  );

  const handleReset = () => {
    setSearchInput("");
    setFilters?.({}, []);
    refetch?.();
  };

  return (
    <div className="flex flex-wrap items-center gap-2 px-3 py-2.5 bg-card border border-border rounded-xl">
      <div className="flex items-center gap-2 h-8 w-full sm:w-[260px] shrink-0 px-2.5 bg-muted/50 border border-border rounded-md">
        <Search className="size-3.5 shrink-0 text-muted-foreground" />
        <input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Rechercher une entreprise, un secteur…"
          className="flex-1 bg-transparent outline-none text-[13px] placeholder:text-muted-foreground"
          aria-label="Rechercher"
        />
        {searchInput && (
          <button
            type="button"
            onClick={() => setSearchInput("")}
            className="text-[11px] text-muted-foreground hover:text-foreground px-1"
            aria-label="Effacer"
          >
            ✕
          </button>
        )}
      </div>

      <div className="hidden sm:block w-px h-4 bg-border shrink-0" />

      {DROPDOWNS.map((d) => {
        const activeValue = filterValues?.[d.filterKey] as
          | string
          | number
          | undefined;
        const activeStr = activeValue != null ? String(activeValue) : undefined;
        const activeOption = d.options.find((o) => o.value === activeStr);
        const isActive = !!activeStr && activeStr !== "";

        return (
          <Popover key={d.id}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className={cn(
                  "inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border text-[13px] whitespace-nowrap shrink-0",
                  isActive
                    ? "bg-emerald-50 border-emerald-200 text-emerald-900 dark:bg-emerald-950/30 dark:border-emerald-900/40 dark:text-emerald-200"
                    : "bg-background border-border text-muted-foreground hover:bg-muted/60 hover:text-foreground",
                )}
              >
                {d.useDotWhenActive && activeOption?.dot ? (
                  <span
                    className={cn("size-1.5 rounded-full", activeOption.dot)}
                  />
                ) : (
                  <span
                    className={cn(
                      "shrink-0",
                      isActive
                        ? "text-emerald-700 dark:text-emerald-300"
                        : "text-muted-foreground",
                    )}
                  >
                    {d.icon}
                  </span>
                )}
                {isActive && activeOption ? (
                  <>
                    <span className="text-muted-foreground font-normal">
                      {d.label} :
                    </span>
                    <span className="font-medium">{activeOption.label}</span>
                  </>
                ) : (
                  <span>{d.label}</span>
                )}
                <ChevronDown className="size-3 text-muted-foreground/70 shrink-0" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              sideOffset={4}
              className="p-1 w-[220px] max-h-[320px] overflow-y-auto"
            >
              {d.options.map((opt) => {
                const selected =
                  (opt.value === null && !isActive) || opt.value === activeStr;
                return (
                  <button
                    key={opt.value ?? "_all"}
                    type="button"
                    onClick={() => applyDropdown(d.filterKey, opt.value)}
                    className={cn(
                      "w-full flex items-center gap-2 px-2 py-1.5 rounded-sm text-[13px] text-left",
                      "hover:bg-muted/60",
                      selected && "bg-muted/40",
                    )}
                  >
                    {opt.dot ? (
                      <span className={cn("size-1.5 rounded-full", opt.dot)} />
                    ) : (
                      <span className="size-1.5" />
                    )}
                    <span className="flex-1">{opt.label}</span>
                    {selected && (
                      <Check className="size-3.5 text-emerald-600 dark:text-emerald-400" />
                    )}
                  </button>
                );
              })}
            </PopoverContent>
          </Popover>
        );
      })}

      {hasActive && (
        <button
          type="button"
          onClick={handleReset}
          className="text-[12.5px] text-muted-foreground hover:text-foreground hover:underline px-1.5 shrink-0"
        >
          Réinitialiser
        </button>
      )}

      <span className="ml-auto text-[12.5px] text-muted-foreground tabular-nums shrink-0">
        <span className="text-foreground">{shownCount}</span> entreprise
        {shownCount > 1 ? "s" : ""} sur {totalCount}
      </span>
    </div>
  );
}
